import { AxiosResponse, isAxiosError } from 'axios'
import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query'
import { NChat } from '../interfaces/services/chat.interfaces'
import {
  ConversationState,
  ConversationType,
  ConversationsResponse,
} from '../generated/api/chat-conversations'
import {
  MessageRow,
  MessagesPage,
  ReadersPage,
  RoomHeader,
  RoomReads,
} from '../generated/api/chat-room'
import {
  AddMembersBody,
  ArchivedResponse,
  CandidatesPage,
  MembersPage,
  PatchRoomBody,
  RoomIdResponse,
} from '../generated/api/chat-room-settings'
import { RecipientsPage } from '../generated/api/chat-recipients'
import { createUrl } from './helpers'
import axiosAuth from './config'

export const getHttpStatus = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined

export const isRoomAccessError = (error: unknown): boolean => {
  const status = getHttpStatus(error)
  return status === 403 || status === 404
}

interface GetConversationsQuery {
  type: ConversationType
  state: ConversationState
  societyIds?: string[]
  page?: number
  limit?: number
}

export const getConversations = (
  query: GetConversationsQuery,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: ConversationsResponse }>> => {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== undefined)
  )
  return axiosAuth.get<{ data: ConversationsResponse }>(
    createUrl('/chat/conversations', params),
    { signal }
  )
}

export const getRoomById = (
  id: string
): Promise<AxiosResponse<NChat.NGetRoomById.IResponse>> => {
  return axiosAuth.get<NChat.NGetRoomById.IResponse>(
    createUrl(`/chat/get-rooms/${id}`, {})
  )
}

export const getUserRoom = (
  id: string
): Promise<AxiosResponse<NChat.NGetRoomByUserId.IResponse>> => {
  return axiosAuth.get<NChat.NGetRoomByUserId.IResponse>(
    createUrl(`/chat/get-rooms/user/${id}`, {})
  )
}

export const createRoom = (
  body: NChat.NCreateRoom.IRequestBody
): Promise<AxiosResponse<NChat.NCreateRoom.IResponse>> => {
  return axiosAuth.post<NChat.NCreateRoom.IResponse>(
    createUrl(`/chat/room`),
    body
  )
}

export const joinRooms = (
  body: NChat.NJoinRooms.IRequestBody
): Promise<AxiosResponse<NChat.NJoinRooms.IResponse>> => {
  return axiosAuth.post<NChat.NJoinRooms.IResponse>(
    createUrl(`/chat/join-rooms`),
    body
  )
}

export const getRoomHeader = (
  roomId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: RoomHeader }>> => {
  return axiosAuth.get<{ data: RoomHeader }>(
    createUrl(`/chat/rooms/${roomId}`),
    { signal }
  )
}

export const getRoomMessagesPage = (
  roomId: string,
  query: { page: number; limit: number },
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: MessagesPage }>> =>
  axiosAuth.get<{ data: MessagesPage }>(
    createUrl(`/chat/rooms/${roomId}/messages`, query),
    { signal }
  )

export interface SendRoomMessageBody {
  text: string
  mediaIds?: string[]
}

export const sendRoomMessage = (
  roomId: string,
  body: SendRoomMessageBody
): Promise<AxiosResponse<{ data: MessageRow }>> => {
  return axiosAuth.post<{ data: MessageRow }>(
    createUrl(`/chat/rooms/${roomId}/messages`),
    body
  )
}

export const getRoomReads = (
  roomId: string,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: RoomReads }>> => {
  return axiosAuth.get<{ data: RoomReads }>(
    createUrl(`/chat/rooms/${roomId}/reads`),
    { signal }
  )
}

export const markRoomRead = (
  roomId: string
): Promise<AxiosResponse<{ data: RoomIdResponse }>> => {
  return axiosAuth.post<{ data: RoomIdResponse }>(
    createUrl(`/chat/rooms/${roomId}/read`)
  )
}

const definedParams = (
  query: Record<string, string | number | undefined>
): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(query).filter(
      (entry): entry is [string, string | number] => entry[1] !== undefined
    )
  )

export const getRoomMembersPage = (
  roomId: string,
  query: { page: number; limit: number },
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: MembersPage }>> => {
  return axiosAuth.get<{ data: MembersPage }>(
    createUrl(`/chat/rooms/${roomId}/members`, query),
    { signal }
  )
}

export const getMessageReaders = (
  roomId: string,
  messageId: string,
  query: { page: number; limit: number },
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: ReadersPage }>> => {
  return axiosAuth.get<{ data: ReadersPage }>(
    createUrl(`/chat/rooms/${roomId}/messages/${messageId}/readers`, query),
    { signal }
  )
}

export const getRoomMemberCandidatesPage = (
  roomId: string,
  query: { search?: string; page: number; limit: number },
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: CandidatesPage }>> => {
  return axiosAuth.get<{ data: CandidatesPage }>(
    createUrl(`/chat/rooms/${roomId}/member-candidates`, definedParams(query)),
    { signal }
  )
}

export type RecipientType = 'oneonone' | 'interests'

export interface RecipientsQueryParams {
  type: RecipientType
  societyId?: string
  search?: string
  userId?: string
  page?: number
  limit?: number
}

export const getRecipientsPage = (
  query: {
    type: RecipientType
    societyId?: string
    search?: string
    userId?: string
    page: number
    limit: number
  },
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: RecipientsPage }>> => {
  return axiosAuth.get<{ data: RecipientsPage }>(
    createUrl('/chat/recipients', definedParams(query)),
    { signal }
  )
}

export const patchRoomSettings = (
  roomId: string,
  body: PatchRoomBody
): Promise<AxiosResponse<{ data: RoomHeader }>> => {
  return axiosAuth.patch<{ data: RoomHeader }>(
    createUrl(`/chat/rooms/${roomId}`),
    body
  )
}

export const addRoomMembers = (
  roomId: string,
  body: AddMembersBody
): Promise<AxiosResponse<{ data: RoomHeader }>> => {
  return axiosAuth.post<{ data: RoomHeader }>(
    createUrl(`/chat/rooms/${roomId}/members`),
    body
  )
}

export const removeRoomMember = (
  roomId: string,
  userId: string
): Promise<AxiosResponse<{ data: RoomHeader }>> => {
  return axiosAuth.delete<{ data: RoomHeader }>(
    createUrl(`/chat/rooms/${roomId}/members/${userId}`)
  )
}

export const leaveRoom = (
  roomId: string
): Promise<AxiosResponse<{ data: RoomIdResponse }>> => {
  return axiosAuth.post<{ data: RoomIdResponse }>(
    createUrl(`/chat/rooms/${roomId}/leave`)
  )
}

export const setRoomArchived = (
  roomId: string,
  archived: boolean
): Promise<AxiosResponse<{ data: ArchivedResponse }>> => {
  return axiosAuth.put<{ data: ArchivedResponse }>(
    createUrl(`/chat/rooms/${roomId}/archived`),
    { archived }
  )
}

export const deleteRoom = (
  roomId: string
): Promise<AxiosResponse<{ data: RoomIdResponse }>> => {
  return axiosAuth.delete<{ data: RoomIdResponse }>(
    createUrl(`/chat/rooms/${roomId}`)
  )
}

export const GET_RECIPIENTS_LIMIT = 40

export const chatQueries = {
  scope: 'chat',
  getRecipients: ({
    type,
    societyId,
    search,
    userId,
    page = 1,
    limit = GET_RECIPIENTS_LIMIT,
  }: RecipientsQueryParams) =>
    queryOptions({
      queryKey: [
        {
          scope: 'chat',
          entity: 'recipients',
          type,
          societyId: societyId ?? null,
          search: search ?? '',
          userId: userId ?? null,
          page,
          limit,
        },
      ] as const,
      queryFn: async ({ queryKey, signal }) =>
        (
          await getRecipientsPage(
            {
              type: queryKey[0].type,
              societyId: queryKey[0].societyId ?? undefined,
              search: queryKey[0].search || undefined,
              userId: queryKey[0].userId ?? undefined,
              page: queryKey[0].page,
              limit: queryKey[0].limit,
            },
            signal
          )
        ).data.data,
      staleTime: 60_000,
    }),
  getUserRoom: (userId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'chat', entity: 'getUserRoom', userId }],
      queryFn: async ({ queryKey }) =>
        (await getUserRoom(queryKey[0].userId!)).data.data,
      enabled: !!userId,
    }),
  getRoomById: (roomId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'chat', entity: 'getRoomById', roomId }],
      queryFn: async ({ queryKey }) =>
        (await getRoomById(queryKey[0].roomId!)).data.data,
      enabled: !!roomId,
    }),
}

const GET_ROOM_MESSAGES_LIMIT = 30
const GET_CONVERSATIONS_LIMIT = 20
const GET_ROOM_MEMBERS_LIMIT = 40

const nextPageOf = (page: {
  currentPage: number
  pages: number
}): number | undefined =>
  page.currentPage + 1 > page.pages ? undefined : page.currentPage + 1

const dedupeMessages = (
  data: InfiniteData<MessagesPage, number>
): InfiniteData<MessagesPage, number> => {
  const seen = new Set<string>()
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.filter((item) => {
        if (seen.has(item.id)) return false
        seen.add(item.id)
        return true
      }),
    })),
  }
}

export const chatRoomQueries = {
  header: (roomId: string) =>
    queryOptions({
      queryKey: [{ scope: 'chat', entity: 'room', roomId }] as const,
      queryFn: async ({ queryKey, signal }) =>
        (await getRoomHeader(queryKey[0].roomId, signal)).data.data,
      staleTime: 60_000,
      retry: (failureCount, error) =>
        !isRoomAccessError(error) && failureCount < 3,
    }),
  messages: (roomId: string) =>
    infiniteQueryOptions({
      queryKey: [{ scope: 'chat', entity: 'roomMessages', roomId }] as const,
      initialPageParam: 1,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (
          await getRoomMessagesPage(
            queryKey[0].roomId,
            { page: pageParam, limit: GET_ROOM_MESSAGES_LIMIT },
            signal
          )
        ).data.data,
      getNextPageParam: (lastPage) =>
        lastPage.nextCursor == null ? undefined : Number(lastPage.nextCursor),
      select: dedupeMessages,
      retry: (failureCount, error) =>
        !isRoomAccessError(error) && failureCount < 3,
    }),
  reads: (roomId: string) =>
    queryOptions({
      queryKey: [{ scope: 'chat', entity: 'roomReads', roomId }] as const,
      queryFn: async ({ queryKey, signal }) =>
        (await getRoomReads(queryKey[0].roomId, signal)).data.data.items,
      retry: (failureCount, error) =>
        !isRoomAccessError(error) && failureCount < 3,
    }),
  members: (roomId: string) =>
    infiniteQueryOptions({
      queryKey: [{ scope: 'chat', entity: 'roomMembers', roomId }] as const,
      initialPageParam: 1,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (
          await getRoomMembersPage(
            queryKey[0].roomId,
            { page: pageParam, limit: GET_ROOM_MEMBERS_LIMIT },
            signal
          )
        ).data.data,
      getNextPageParam: nextPageOf,
      staleTime: 60_000,
      retry: (failureCount, error) =>
        !isRoomAccessError(error) && failureCount < 3,
    }),
  readers: (roomId: string, messageId: string) =>
    infiniteQueryOptions({
      queryKey: [
        { scope: 'chat', entity: 'roomReaders', roomId, messageId },
      ] as const,
      initialPageParam: 1,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (
          await getMessageReaders(
            queryKey[0].roomId,
            queryKey[0].messageId,
            { page: pageParam, limit: GET_ROOM_MEMBERS_LIMIT },
            signal
          )
        ).data.data,
      getNextPageParam: nextPageOf,
      staleTime: 60_000,
      retry: (failureCount, error) =>
        !isRoomAccessError(error) && failureCount < 3,
    }),
  memberCandidates: (roomId: string, search: string) =>
    infiniteQueryOptions({
      queryKey: [
        { scope: 'chat', entity: 'roomMemberCandidates', roomId, search },
      ] as const,
      initialPageParam: 1,
      queryFn: async ({ pageParam, queryKey, signal }) =>
        (
          await getRoomMemberCandidatesPage(
            queryKey[0].roomId,
            {
              search: queryKey[0].search || undefined,
              page: pageParam,
              limit: GET_ROOM_MEMBERS_LIMIT,
            },
            signal
          )
        ).data.data,
      getNextPageParam: nextPageOf,
      placeholderData: keepPreviousData,
      staleTime: 0,
    }),
}

export const chatConversationsQueryKey = [
  { scope: 'chat', entity: 'conversations' },
] as const

export const chatRecipientsQueryKey = [
  { scope: 'chat', entity: 'recipients' },
] as const

export const chatMemberCandidatesQueryKey = [
  { scope: 'chat', entity: 'roomMemberCandidates' },
] as const

export interface ConversationsQueryParams {
  type: ConversationType
  state: ConversationState
  societyIds?: string[]
}

export const infiniteChatQueries = {
  conversations: ({ type, state, societyIds }: ConversationsQueryParams) =>
    infiniteQueryOptions({
      queryKey: [
        {
          ...chatConversationsQueryKey[0],
          type,
          state,
          societyIds: societyIds?.length ? societyIds : null,
        },
      ] as const,
      initialPageParam: 1,
      queryFn: async ({ pageParam, signal }) =>
        (
          await getConversations(
            {
              type,
              state,
              societyIds: societyIds?.length ? societyIds : undefined,
              page: pageParam,
              limit: GET_CONVERSATIONS_LIMIT,
            },
            signal
          )
        ).data.data,
      getNextPageParam: nextPageOf,
    }),
}
